import clsx from "clsx";
import React, { useRef, useState, useEffect } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import QRCode from 'react-qr-code'
import { ITab, ITabs } from "../../types/customizer";
import MyGallery from "../MyGallery";
import "./index.scss";
import PrintSummary from "../PrintSummary";
import {
  REQUIRED_TAB_FIELDS,
  TABS_TO_AVOID,
  getRowContent,
} from "../../../utils/summary";
import { useRingCustomizerContext } from "../../context/RingCustomizerContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AnyObject, APP_STYLES, DEFAULT_STYLES } from "../../../utils/basic";
import If from "../If";
import { ReactComponent as QRIcon } from '../../../assets/images/qr_icon.svg';
import Accordion from "../AccordionNew";
import ProductDisclaimers from "../ProductDisclaimers";

declare global {
  interface Window {
    gladlyConfig?: { appId?: string; [k: string]: any };
  }
}

interface SummaryProps {
  images?: string[];
  tabs: ITabs;
  isPDP?: boolean;
}

export const formatSummaryDate = (date: string) => {
  const momentDate = moment(date);
  const formatted = momentDate.format('ddd. MMM. Do*');

  if (momentDate.isSame(moment(), 'day')) {
    return `Today (${formatted})`;
  } else if (momentDate.isSame(moment().add(1, 'day'), 'day')) {
    return `Tomorrow (${formatted})`;
  }
  return formatted; // Default format
};

export const getLastestShippingDate = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter(date => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map(date => moment(date)));
  return earliestDate.format('ddd. MMM. Do*')
}

export const getLastestShippingDateHenne = (shippingOptions: AnyObject) => {
  const dates = Object.values(shippingOptions || {}).filter(date => moment(date, 'YYYY-MM-DD', true).isValid());
  const earliestDate = moment.min(dates.map(date => moment(date)));
  return earliestDate.format('ddd, MMM Do, YYYY*')
}

const Summary = ({ images = [], tabs, isPDP = false }: SummaryProps) => {
  const { product, subpath, params, isQueryHandling, autoPrint, clearAutoPrint } = useRingCustomizerContext();
  const [value, setValue] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const [preparingPrint, setPreparingPrint] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const shipDateRef = useRef<HTMLDivElement>(null);

  const triggerShipDatePulse = () => {
    const el = shipDateRef.current;
    if (el) {
      el.classList.remove('tw-animate-pulse-once');
      void el.offsetWidth;
      el.classList.add('tw-animate-pulse-once');
      const onEnd = () => {
        el.classList.remove('tw-animate-pulse-once');
        el.removeEventListener('animationend', onEnd);
      };
      el.addEventListener('animationend', onEnd);
    }
  };

  useEffect(() => {
    if ((window as any).__highlightShipDate) {
      (window as any).__highlightShipDate = false;
      triggerShipDatePulse();
    }
  });

  useEffect(() => {
    const handleHighlight = () => { triggerShipDatePulse(); };
    window.addEventListener('highlight-ship-date', handleHighlight);
    return () => { window.removeEventListener('highlight-ship-date', handleHighlight); };
  }, []);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Summary",
    bodyClass: "print-window",
    onAfterPrint: () => setPreparingPrint(false),
  });

  const handlePrintWithSpinner = () => {
    setPreparingPrint(true);
    setTimeout(() => {
      handlePrint();
    }, 300);
  };

  useEffect(() => {
    if (!autoPrint) return;
    setPreparingPrint(true);
    const timeout = setTimeout(() => {
      handlePrint();
      clearAutoPrint();
    }, 300);
    return () => clearTimeout(timeout);
  }, [autoPrint, handlePrint, clearAutoPrint]);

  const containerClass = clsx({
    "tw-overflow-y-auto tw-overflow-x-hidden tw-h-full swatch-filter-border tw-pt-3 sm:tw-pt-10 tw-px-5":
      !isPDP,
    "tw-w-full tw-h-full tw-py-1 tw-pb-4 tw-bg-blue-50 tw-text-xs summary-pdp":
      isPDP,
  });

  const innerContainerClass = clsx("tw-text-prussian-blue", {
    "tw-w-full lg:tw-w-3/5 lg:tw-pr-8 xl:tw-w-2/3 xl:tw-pr-[15%] 2xl:tw-pr-[20%]": !isPDP,
    "tw-w-full": isPDP,
  });

  const handleSearch = async() => {
    try {
      const response  = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/rings/searchMagicNumber?magicNumber=${value}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      }).then(res => res.ok ? res.json() : Promise.reject(res));
      const customizeUrl = response.ring_data?.customize_url;
      const bestseller_id = response.ring_data?.bestseller_id;
      if(bestseller_id) {
          // When there is a bestseller id, we append to current URL
          window.location.href += `&bestSeller=${bestseller_id}`;
      }else if(customizeUrl) {
          navigateToUrl(customizeUrl);
          setValue('');
      } else {
          toast.error(response.message)
      }
      console.log(response);
    } catch(error) {
      console.log(error)
    }
  }

  const navigateToUrl = (URL: string, replace = true) => {
    const { pathprefix, subpath} = params.pathParams;
    let navigateUrl = URL;
    if(pathprefix && subpath) {
      navigateUrl = `/${pathprefix}/${subpath}${URL}`;
    } else if(subpath){
      navigateUrl = `/${subpath}${URL}`;
    }

    if(isQueryHandling) {
      navigateUrl = `${location.pathname}?ring_url=${encodeURIComponent(navigateUrl)}`;
    }

    const otherQueryString = new URLSearchParams(params.otherQueryParams).toString();
    if(otherQueryString) {
      navigateUrl += `&${otherQueryString}`;
    }
    navigate(navigateUrl, { replace: replace }); // After URL is navigated it calls a useffect of location dependencies
  }
  
  const getRingInfo = () => {
    return (
      <>
        <div className={clsx("summary-container tw-mt-3 tw-ml-0")}>
          <div>
            <p className='summaryLabel'>Style</p>
            <p className='summaryValue'>{product?.sku}</p>
          </div>
          {REQUIRED_TABS.map((tab: ITab, index) =>
            (<React.Fragment key={index}> { getRowContent(tab, index, { isPDP }) }</React.Fragment>))
          }
        </div>
        <div className={clsx("summary-container tw-mt-3 tw-ml-0")}>
          {ADDITIONAL_TABS.map((tab: ITab, index) =>
            (<React.Fragment key={index}> { getRowContent(tab, index, { isPDP }) }</React.Fragment>))
          }
        </div>
      </>
    )
  }

  const REQUIRED_TABS = tabs.filter((tab: ITab) => REQUIRED_TAB_FIELDS.includes(tab.eventKey) && (!['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath) || tab.eventKey != 'ringSize'));
  const ADDITIONAL_TABS = tabs.filter((tab: ITab) => !REQUIRED_TAB_FIELDS.includes(tab.eventKey) && !TABS_TO_AVOID.includes(tab.eventKey));
  const styles = APP_STYLES[subpath] || DEFAULT_STYLES;

  useEffect(() => {
    // Only load for these subpaths
    const shouldLoad = ['daniels-jewelers','daniels-jewelers-dev'].includes(subpath);
    if (!shouldLoad) return;

    const src = "https://static.klaviyo.com/onsite/js/QVhRw2/klaviyo.js?company_id=QVhRw2";

    // Prevent duplicates (StrictMode mounts twice in dev)
    if (document.querySelector(`head script[src="${src}"]`)) return;

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.setAttribute("data-loaded-by", "Summary");
    document.head.appendChild(script);

    // Optional cleanup: remove when leaving this page
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [subpath]);

  useEffect(() => {
    const shouldLoad = ['legacy-touch', 'legacy-touch-dev'].includes(subpath);
    if (!shouldLoad || typeof window === 'undefined' || typeof document === 'undefined') return;

    // 1) Provide config BEFORE loading the widget
    window.gladlyConfig = {
      ...(window.gladlyConfig || {}),
      appId: 'tributetech.com-LEGACYTOUCH.COM',
    };

    const src = "https://cdn.gladly.com/chat-sdk/widget.js?q=1759954900670";

    // Prevent duplicates (StrictMode mounts twice in dev)
    if (document.querySelector(`head script[src="${src}"]`)) return;

    // 2) Inject the widget
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute('data-gladly-src', src);
    document.head.appendChild(script);

  }, [subpath]);

  return (
    <div className={containerClass} style={{ position: 'relative' }}>
      {preparingPrint && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, gap: '12px' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--secondary-border-color, #e9ecef)', borderTopColor: 'var(--brand-color, #2a77a7)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span className="tw-text-lg tw-text-primary-color">Preparing to print...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div className={clsx("tw-flex tw-flex-wrap summary", {"xl:tw-w-11/12 tw-ml-auto": !isPDP,})}>
        <div className={innerContainerClass}>
          <If condition={subpath === 'frank-adams'}>
            <p className='tw-text-sm tw-mb-4 tw-font-bold tw-text-primary-color'>Please print this summary and bring it with you to Frank Adams Jewelers. We will verify your finger size and be happy to place your order.</p>
          </If>
          <If condition={['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath)}>
            <div className="tw-mb-4">
              <ProductDisclaimers description={product?.description || ''} showLearnMoreLinks={subpath === 'revolution'} />
            </div>
          </If>
          <If condition={['legacy-touch', 'legacy-touch-dev'].includes(subpath) && styles.showShippingEstimation}>
            <div className='tw-mb-2 tw-font-bold'>Usually ships within 2 weeks</div>
          </If>
          {!isPDP && ['daniels-jewelers', 'daniels-jewelers-dev'].includes(subpath) && (<>
            <button id="print-btn" className="tw-mb-5 !tw-bg-brand-color !tw-text-white hover:tw-text-white tw-px-9 tw-py-2 tw-font-medium tw-normal-case secondary-font-family focus:tw-outline-none" onClick={handlePrintWithSpinner}>Print Summary</button>
            <PrintSummary tabs={tabs} image={images?.[0]} product={product} ref={componentRef} />
          </>)}
            {/* Search Magic Number */}
            <If condition={!['daniels-jewelers','daniels-jewelers-dev'].includes(subpath)}>
              <div className="tw-mt-4 tw-flex xs:tw-flex-col sm:tw-flex-row tw-gap-4">
                <input type="text" name="magic_number" placeholder="000-03C9-10" value={value} onChange={(e) => {const formatted = e.target.value.replace(/O/gi, "0"); setValue(formatted); }} className="tw-p-3 tw-border tw-mr-3 xs:!tw-w-50 sm:!tw-w-40 xs:tw-m-auto sm:tw-m-0" />
                <button id="search-btn" className="tw-btn-brand tw-btn-sm tw-w-50 xs:tw-m-auto sm:tw-m-0" onClick={handleSearch}>Search Magic Number</button>
              </div>
            </If>

          {/* Benari Jewelers sizing kit info */}
          <If condition={subpath === 'benari-jewelers'}>
            <div className="tw-mt-6 tw-text-sm tw-px-1">
              <p className="tw-mb-2">This ring includes a free at-home sizing kit that will arrive in 1-3 business days. You will be able to finalize your ring size after receiving the kit.</p>
              <p className="tw-mb-2">Free Standard Delivery: estimated delivery 4-6 weeks</p>
              <p className="tw-mb-2">Don't see your size available? You can special order a size. Contact us.</p>
              <p className="tw-mb-2">Please note, all custom jewelry sales are final.</p>
            </div>
          </If>

          {/* SHIP DATE SECTION */}
          <If condition={styles.showShippingEstimation && !['legacy-touch', 'legacy-touch-dev'].includes(subpath)}>
            <div ref={shipDateRef} className="tw-mt-6 tw-overflow-hidden" style={{ border: '1px solid var(--primary-border-color)', borderRadius: '4px' }}>
              <div className="tw-text-lg tw-font-medium tw-px-6 tw-pt-5 tw-pb-3">Ship Date</div>
              <div className="tw-px-6 tw-pb-3">
                {product?.shipping_options?.same_day ? <div className="tw-flex tw-justify-between tw-mt-1">
                  <p className='tw-font-medium'>Same Day</p>
                  <p>{formatSummaryDate(product?.shipping_options?.same_day)}</p>
                </div> : null}
                {product?.shipping_options?.next_day ? <div className="tw-flex tw-justify-between tw-mt-1">
                  <p className='tw-font-medium'>Next Day</p>
                  <p>{formatSummaryDate(product?.shipping_options?.next_day)}</p>
                </div> : null}
                <If condition={!['kay-jewelers'].includes(subpath)}>
                  {product?.shipping_options?.rush ? <div className="tw-flex tw-justify-between tw-mt-1">
                    <p className='tw-font-medium'>Rush</p>
                    <p>{formatSummaryDate(product?.shipping_options?.rush)}</p>
                  </div> : null}
                </If>
                {product?.shipping_options?.standard ? <div className="tw-flex tw-justify-between tw-mt-1">
                  <p className='tw-font-medium'>Standard</p>
                  <p>{formatSummaryDate(product?.shipping_options?.standard)}</p>
                </div> : null}
              </div>
              <div className='tw-px-6 tw-pb-5 tw-text-xs'>* Contact us if you have any questions, need a rush, or need a ring by a specific date sooner than what's displayed.</div>
            </div>
          </If>

          {/* Ring Details */}
          <If condition={isPDP}>
            {getRingInfo()}
          </If>
          <If condition={!isPDP}>
            <div className="tw-mt-6 tw-overflow-hidden" style={{ border: '1px solid var(--primary-border-color)', borderRadius: '4px' }}>
              <div className="tw-text-lg tw-font-medium tw-px-6 tw-pt-5 tw-pb-3">Information for Ordering</div>
              <If condition={subpath === 'rogers-jewelry'}>
                <div className="tw-px-6 tw-pb-2 tw-text-sm">(Please provide us with this magic number for your design)</div>
              </If>
              {/* Magic Number */}
              <div className="tw-flex tw-my-2 tw-px-6">
                <p className='tw-w-40 tw-shrink-0 tw-mr-3'>Magic Number</p>
                <p>{product?.magic_number}</p>
              </div>

              {/* Description */}
              <div className="tw-flex tw-mt-2 tw-mb-5 tw-px-6">
                <p className='tw-w-40 tw-shrink-0 tw-mr-3'>Description</p>
                <p>{product?.description}</p>
              </div>
            </div>

            <Accordion title="See Additional Details" className="tw-mt-6 tw-rounded-lg tw-bg-white summary-accordian" style={{ border: '1px solid var(--primary-color)', backgroundColor: '#fff' }}>
              {getRingInfo()}
            </Accordion>
          </If>

          <If condition={['daniels-jewelers','daniels-jewelers-dev'].includes(subpath)}>
            <div className="klaviyo-form-Tvh5eL" style={{marginTop: "20px", borderBottom : "solid 1px", borderTop : "solid 1px"}}></div>

            {/* Search Magic Number */}
            <div className="tw-mt-8 tw-flex xs:tw-flex-col sm:tw-flex-row tw-gap-4">
              <input type="text" name="magic_number" placeholder="000-03C9-10" value={value} onChange={e => { setValue(e.target.value); }} className="tw-p-3 tw-border tw-mr-3 xs:!tw-w-50 sm:!tw-w-40 xs:tw-m-auto sm:tw-m-0" />
              <button id="search-btn" className="tw-btn-brand tw-btn-sm tw-w-50 xs:tw-m-auto sm:tw-m-0" onClick={handleSearch}>Search Magic Number</button>
            </div>
          </If>        

          {!isPDP && !['revolution', 'stonebrook-jewelry', 'foundry', 'daniels-jewelers', 'daniels-jewelers-dev'].includes(subpath) && (<>
            <button id="print-btn" className="tw-btn-brand tw-btn-sm tw-w-40 tw-my-6" onClick={handlePrintWithSpinner}>Print Summary</button>
            <PrintSummary tabs={tabs} image={images?.[0]} product={product} ref={componentRef} />
          </>)}
        </div>

        <If condition={!isPDP}>
          <div className="tw-flex-1 gallery_container tw-relative tw-hidden lg:tw-flex tw-flex-col">
            <MyGallery images={images} showThumbnail={false} />
            {/* QR Code Sharing Section */}
            <div className="tw-mt-6 xl:tw-w-3/4 tw-m-auto">
              {/* Top Section: Icon/Text and QR Code */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                {/* Selfie Icon and Text - Centered */}
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-flex-1">
                  <QRIcon
                    className="tw-w-20 tw-h-20 tw-text-primary-color"
                  />
                  <span className="tw-text-prussian-blue tw-text-sm tw-italic tw-text-center tw-leading-tight">
                    Scan to take your design with you
                  </span>
                </div>

                {/* QR Code - Far Right */}
                <div className="tw-flex-1">
                  <QRCode
                    value={`${window.location.origin}${location.pathname}${location.search}`}
                    style={{ width: '100%', height: 'auto' }}
                    size={256}
                    fgColor="var(--primary-color)"
                  />
                </div>
              </div>


              {/* Dividing Line */}
              {/* <div className="gold-divider tw-mt-6"></div> */}

              {/* Email Input and Share Button */}
              {/* <div className="tw-w-full tw-flex tw-gap-2 tw-max-w-sm tw-mt-5">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email"
                  className="tw-flex-[2] tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2.5 tw-text-sm"
                />
                <button
                  className="tw-btn-brand tw-btn-sm tw-flex-1 tw-whitespace-nowrap"
                  onClick={handleShare}
                >
                  Share This Style
                </button>
              </div> */}
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};

export default Summary;
